/**
 * App module
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2024 Build Centrix
 */
import { ApplicationRef, DoBootstrap, Injector, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { setUpLocationSync } from '@angular/router/upgrade';
import { UpgradeModule } from '@angular/upgrade/static';
import 'angular-moment';
import {
  helpers as helpersLibraryService,
  services as mainLibraryService,
} from 'main-library-js';
import wdEnvironmentService from 'wd-library-environment';
import { WdLocalEnvironmentService } from './services/environment/wd-local-environment.service';

import { MatDialog } from '@angular/material/dialog';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SharedModule } from './components/shared.module';

import { CommonModule, DatePipe } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { BlankApp } from './angularjs/angularjsapp';
import { AuthGuard } from './guards/auth.guard';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

/**
 * @see https://github.com/ngx-translate/core
 */
export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}
@NgModule({
  declarations: [AppComponent],
  imports: [
    AppRoutingModule,
    BrowserModule,
    CommonModule,
    BrowserAnimationsModule,
    UpgradeModule,
    SharedModule,
    HttpClientModule,
    TranslateModule.forRoot({
      defaultLanguage: 'EN',
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    FormsModule,
    ReactiveFormsModule,
  ],
  providers: [
    AuthGuard,
    MatDialog,
    DatePipe,
    { provide: '$scope', useExisting: '$rootScope' },
    {
      provide: 'wdEnvironmentService',
      useFactory: (i: any) => i.get('wdEnvironmentService'),
      deps: ['$injector'],
    },
    {
      provide: 'wdHttpService',
      useFactory: (i: any) => i.get('wdHttpService'),
      deps: ['$injector'],
    },
    {
      provide: 'wdOauth2HttpService',
      useFactory: (i: any) => i.get('wdOauth2HttpService'),
      deps: ['$injector'],
    },
    {
      provide: 'bcxOauth2HttpService',
      useFactory: (i: any) => i.get('bcxOauth2HttpService'),
      deps: ['$injector'],
    },
    ...Object.keys(mainLibraryService).map((key) => {
      return {
        provide: key,
        useFactory: (i: any) => i.get(key),
        deps: ['$injector'],
      };
    }),
    ...Object.keys(helpersLibraryService).map((key) => {
      return {
        provide: key,
        useFactory: (i: any) => i.get(key),
        deps: ['$injector'],
      };
    }),
  ],
})
export class AppModule implements DoBootstrap {
  /**
   * Constructor
   */
  constructor(
    private upgradeModule: UpgradeModule,
    public injector: Injector,
  ) {}

  /**
   * Do bootstrapping
   */
  public async ngDoBootstrap(ref: ApplicationRef) {
    this.upgradeModule.bootstrap(document.body, [
      BlankApp.name,
      wdEnvironmentService.name,
      ...Object.values(mainLibraryService),
      ...Object.values(helpersLibraryService),
    ]);
    await this.initialize();
    ref.bootstrap(AppComponent);
    setUpLocationSync(this.upgradeModule); // https://github.com/angular/angular/issues/14081
  }

  /**
   * Initialize wd-environment-service
   */
  private async initialize(): Promise<void> {
    await this.injector.get(WdLocalEnvironmentService).initialize();
  }
}

// EOF
